import React, { useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import Cookies from "js-cookie";

import ProductGrid from "./ProductGrid";
import FilterBar from "../Filters/FilterBar";
import EmptyState from "../Filters/EmptyState";
import useAppStore from "../../Store/SearchStore";
import usePersonaStore from "../../Store/PersonaStore";
import { encodeSearchText } from "../../Utils/urlEncoder.js";
import PersonaNotification from "../Persona/PersonaNotification";
import { useProductFilters } from "../../Hooks/useProductFilters";
import { useFilterOptions } from "../../Hooks/useFilterOptions";
import { useScrollIntoView } from "../../Hooks/useScrollIntoView";
import { COUNTRY_FILTERS } from "../../config/filters";

/**
 * FilteredProductList - Main component for displaying filtered products
 * Handles filtering, sorting, and displaying products with appropriate empty states
 * 
 * @component
 * @example
 * <FilteredProductList gifts={products} />
 */
export default function FilteredProductList({ gifts }) {
  const userLocation = Cookies.get("X-countryName");
  const { setSearchText, isEmptyResult } = useAppStore();
  const { selectedPersona } = usePersonaStore();
  const history = useHistory();

  const [selectedSort, setSelectedSort] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_FILTERS.ALL);
  const targetRef = useRef(null);

  // Custom hooks
  const filteredProducts = useProductFilters(gifts, {
    sortType: selectedSort,
    countryFilter: selectedCountry,
  });

  const { sortOptions, countryOptions } = useFilterOptions(userLocation);

  // useScrollIntoView(targetRef, []); // Removed redundant scroll to avoid jumpy behavior during explanations hit

  // Handle search text change
  const handleSearchChange = (query) => {
    setSearchText(query);
    const encoded = encodeSearchText(query);
    history.replace(`/search?query=${encoded}`);
  };

  // Determine if country filter should be shown
  const showCountryFilter =
    userLocation && gifts && gifts.some((p) => p.country === userLocation);

  const hasProducts = filteredProducts && filteredProducts.length > 0;
  const hasNoResults = !isEmptyResult && filteredProducts.length === 0;

  return (
    <div>
      {/* Filter Controls */}
      {hasProducts && (
        <FilterBar
          sortValue={selectedSort}
          countryValue={selectedCountry}
          onSortChange={setSelectedSort}
          onCountryChange={setSelectedCountry}
          sortOptions={sortOptions}
          countryOptions={countryOptions}
          showCountryFilter={showCountryFilter}
        />
      )}

      {/* Persona Notification */}
      {hasProducts && selectedPersona && <PersonaNotification />}

      {/* Empty States */}
      <EmptyState isEmpty={isEmptyResult} hasNoResults={hasNoResults} />

      {/* Product Grid */}
      {hasProducts && (
        <ProductGrid
          products={filteredProducts}
          onSearchTextChange={handleSearchChange}
          scrollTargetRef={targetRef}
          variant="search"
        />
      )}
    </div>
  );
}
